import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Control, Form, actions } from 'react-redux-form';
import { connect } from 'react-redux';

import LinkButton from 'components/LinkButton/LinkButton';
import { duckify, filterDuck, singleDuck, withForm } from 'utils/duckify';
import { sequence } from 'utils/numbers';
import FontAwesome from 'utils/FA';
import { ConfirmDisable, ConfirmEnrollment } from './AutopayConfirmModals';
import DateSelector from '../forms/DateSelector';
import ModalComponent from '../ModalComponent';
import ModalRoot from '../ModalRoot';
import Placeholder from '../Placeholder';
import ValidationErrors from '../forms/ValidationErrors';

import {
  billingAccounts as billingAccountsDuck, billingCategories,
  billingConfigForm as billingConfigFormDuck,
  billingConfigs as billingConfigDuck,
  customers as customersDuck,
  modals,
  schoolYears as schoolYearsDuck,
} from '../../ducks';

import './styles/BillingConfig.scss';
import { pluralize } from '../../utils';
import FA from 'utils/FA';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import InfoTooltip from '../shared/InfoTooltip';
import { ordinalizeBillingDay, selectableBillingDays } from '../billing/AutopayDateEditor';

const ResetAccountModal = connect()(({ account, dispatch, saving }) => {
  const buttons = [
    <button className="btn btn-danger" disabled={saving} onClick={() => {
        dispatch(modals.actions.update({saving: true}));
        const token = $('meta[name="csrf-token"]').attr('content');
        fetch(`/api/billing_accounts/${account.id}/reset`, {
          method: 'POST',
          headers: {'X-CSRF-Token': token},
          credentials: 'same-origin'
        }).then(response => {
          if (response.ok) {
            response.json().then(json => dispatch(billingAccountsDuck.actions.cacheSingle(json['billing_account'])));
            Utility.successMessage('Billing account has been reset.');
          } else {
            Utility.errorMessage('Sorry, an error occurred while processing your request.');
          }
          dispatch(modals.actions.hide());
        });
      }}>{saving ? 'Resetting...' : 'Reset'}</button>
  ];

  return <ModalComponent title="Reset Account"
                         buttons={buttons}
                         closeText="Cancel">
    <p>The account status for <em>{account.contact.name}</em> will be rolled back and its ledger items deleted.
      This allows the billing cycle to be rerun for testing purposes.</p>
  </ModalComponent>
});

ResetAccountModal.propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string
  }),
  dispatch: PropTypes.func,
  saving: PropTypes.bool
};

ResetAccountModal.defaultProps = {
  account: {},
  dispatch: () => false,
  saving: false
};

const BillingDayConfirmModal = ({ onClose, onSave }) => {
  const [notify, setNotify] = useState(null);
  const notifyOnChange = (e) => setNotify(e.currentTarget.value);
  const buttons = [
    <button className="btn btn-primary" disabled={notify == null} onClick={() => {onSave(notify === "1")}}>Save</button>
  ];

  return <ModalComponent title="Billing Day Changed" buttons={buttons} closeText="Cancel" onClose={onClose}>
    <p>
      <i className="fa fa-exclamation-triangle red"/>
      &nbsp;
      <strong className='red'>Warning: All accounts in the system that have the default date as their billing date will be updated.</strong>
    </p>

    <p>
      This operation may take a long time to complete. It will run in the background when you click <em>Save</em>.
    </p>

    <p>
      Notify account holders of this change by email?
    </p>

    <ul className="billing-day-confirm-modal-options">
      <li>
        <input name="notify" id="notify-yes" type="radio" value="1" checked={notify === "1"} onChange={notifyOnChange}/>
        <label htmlFor="notify-yes">Yes, send an email to each account holder</label>
      </li>
      <li>
        <input name="notify" id="notify-no" type="radio" value="0" checked={notify === "0"} onChange={notifyOnChange}/>
        <label htmlFor="notify-no">No, don't send an email</label>
      </li>
    </ul>
  </ModalComponent>;
};

const RevenueRecognitionConfirmModal = ({  onSave }) => {

  const doSave = () => {
  // save and hide modal

      onSave(true);

    }
    const buttons = [
        <button className="btn btn-primary" onClick={() => {doSave()}}>Ok</button>
    ];

    return <ModalComponent title="Revenue Recognition Dates Changed" buttons={buttons} closeText="Cancel">
        <p>
        <i className="fa fa-exclamation-triangle red"/>
        &nbsp;
        <strong className='red'>Warning: All Billing Categories will have their Revenue Recognition start and end months updated to the new values set here.  Click 'Ok' to confirm.</strong>
        </p>
    </ModalComponent>;
}

BillingDayConfirmModal.propTypes = {
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

BillingDayConfirmModal.defaultProps = {
  onClose: () => false,
  onSave: () => false
};

const NotificationDaysSelect = ({ model, values, disabled }) => (
  <Control.select model={model} disabled={disabled}>
    {values.map(val => <option key={val.value} value={val.value}>{val.label}</option>)}
  </Control.select>
);

NotificationDaysSelect.propTypes = {
  model: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  values: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }))
};

NotificationDaysSelect.defaultProps = {
  disabled: false,
  values: sequence(0, 15).map(i => ({ label: i === 0 ? 'Disabled' : `${i} days`, value: i }))
};

const BillingConfig = ({
  appConfig,
  billingAccounts,
  billingConfig,
  billingConfigForm,
  duckMeta,
  reset,
  save,
  showAudits,
  lateFeeBillingCategories,
  schoolYears,
  customer,
  dispatch
}) => {
  const { railsEnv } = appConfig;

  const requireAutopayTooltip = (
    <React.Fragment>
      Require Autopay
      <OverlayTrigger
        placement='right'
        overlay={(
          <Tooltip id='tooltip-require-autopay'>
            Enabling this setting will require payers to enroll into autopay when making a payment and
            automatically enable autopay for any account with a saved payment method.
            We recommend that you revisit your Autopay Terms & Conditions if you enable this setting. They can be found
            in Settings >
            Portal > Parent Portal Content.
          </Tooltip>
        )}
      >
        <FontAwesome name='info-circle' preNbsp/>
      </OverlayTrigger>
    </React.Fragment>
  );

  const disableAutopayTooltip = (
    <React.Fragment>
      Disable Autopay
      <OverlayTrigger
        placement='right'
        overlay={(
          <Tooltip id='tooltip-disable-autopay'>
            Enabling this setting will remove the ability for a payer to enroll in autopay and disable autopay for any
            account with autopay enabled.
          </Tooltip>
        )}
      >
        <FontAwesome name='info-circle' preNbsp/>
      </OverlayTrigger>
    </React.Fragment>
  );

  const parentFacingPaymentOptionsTooltip = (
    <React.Fragment>
      Parent Facing Additional Payment Options
      <OverlayTrigger
        placement='right'
        overlay={(
          <Tooltip id='tooltip-parent-facing'>
            Parents will be allowed to select any of the options you make available within the parent portal. They will
            only see the date options defaulted above.
          </Tooltip>
        )}
      >
        <FontAwesome name='info-circle' preNbsp/>
      </OverlayTrigger>
    </React.Fragment>
  );


  const defaultAutopayTooltip = (
    <React.Fragment>
      Preselect Autopay Checkbox
      <InfoTooltip preNbsp>
        Enabling this setting requires users to specifically opt out of autopay when making a payment.
        Disabling this setting requires users to manually opt in to autopay by clicking the checkbox when making a
        payment.
        Users who have already selected an autopay status will not be affected.
      </InfoTooltip>
    </React.Fragment>
  );

  const requireAutopayZeroDepositTooltip = (
    <React.Fragment>
      Require Autopay for $0 Deposits
      <InfoTooltip preNbsp>
        Enabling this setting requires users to enroll in autopay even if their contract has no deposit payment.
      </InfoTooltip>
    </React.Fragment>
  )

  const autopayDayChanges = (
    <span className='billing-config-autopay-changelog'>
      {
        <LinkButton
          content='View Changes'
          onClick={() => showAudits(billingConfig.autopay_config_id, undefined, 'customer_autopay_configs')}
        />
      }
    </span>
  );

  const validationFieldNames = {
    bi_monthly_default: '2 Monthly Withdrawals',
    four_monthly_default: '4 Monthly Withdrawals',
    test_effective_date: 'Effective Date',
    overdue_2_days: 'Account Overdue - Second Notice',
    overdue_3_days: 'Account Overdue - Third Notice',
    statement_1_days: 'Upcoming Statement',
    statement_2_days: 'Upcoming Statement - Second Notice',
    statement_3_days: 'Upcoming Statement - Third Notice',
    ledger_lock: '',
  };

  const topRef = useRef();
  const hasError = (field) => billingConfigForm.model.errors && billingConfigForm.model.errors[field];

  const saveButton = billingConfigForm && (
    <div className='base-setup-fields'>
      <Button
        size='md'
        className='btn btn-primary'
        type='submit'
        disabled={billingConfigForm.saving}
      >
        {billingConfigForm.saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );

  const onSubmit = () => {
    const { revenue_recognition_start_month, revenue_recognition_end_month, revenue_recognition_strategy } = billingConfigForm.model;
    const originalStartMonth = billingConfig.revenue_recognition_start_month;
    const originalEndMonth = billingConfig.revenue_recognition_end_month;
    const originalStrategy = billingConfig.revenue_recognition_strategy;

    const hasRevenueRecognitionChanged = (
      revenue_recognition_start_month !== originalStartMonth ||
      revenue_recognition_end_month !== originalEndMonth ||
      revenue_recognition_strategy !== originalStrategy
    );

    if (hasRevenueRecognitionChanged) {
      dispatch(modals.actions.show('RevenueRecognitionConfirmModal', {
        onSave: (confirmed) => {
          if (confirmed) {
            save(billingConfigForm)
              .then(() => {
                dispatch(modals.actions.hide());
                topRef.current.scrollIntoView();
              });
          }
        },
        onClose: () => {
          // Handle modal close if needed
        }
      }));
    } else {
      save(billingConfigForm)
        .then(() => {
          topRef.current.scrollIntoView();
        });
    }
  };

  const MONTHS = [
    [1, 'January'],
    [2, 'February'],
    [3, 'March'],
    [4, 'April'],
    [5, 'May'],
    [6, 'June'],
    [7, 'July'],
    [8, 'August'],
    [9, 'September'],
    [10, 'October'],
    [11, 'November'],
    [12, 'December']
  ];

  const updateFiscalYearEndMonth = (e) => {
    const startMonth = e.currentTarget.value;

    if (startMonth === '1') {
      dispatch(actions.change('billingConfigForm.model.fiscal_year_end_month', 12));
    } else {
      dispatch(actions.change('billingConfigForm.model.fiscal_year_end_month', startMonth - 1));
    }
  }

  return (
    <React.Fragment>
      <h2 ref={topRef}>Billing Setup</h2>

      {!billingConfig.start_year && (
        <div className='alert'>
          <strong>Warning!</strong>
          &nbsp;Billing will be disabled until a&nbsp;
          <em>Start Year</em>
          &nbsp;is selected.
        </div>
      )}

      <Placeholder.loading
        ready={duckMeta.billingConfigForm.ready}
        content={() => (
          <Form model={billingConfigFormDuck.model} onSubmit={onSubmit}>
            <div className='billing-general-settings'>
              <ValidationErrors
                errors={billingConfigForm.model.errors}
                fieldNames={validationFieldNames}
              />
              <div className='base-setup-fields'>
                <div className='base-setup-field-group'>
                  <div className='base-setup-small-field-label'>Default Billing Day</div>
                  <div className='base-setup-field-inline-label'>Additional Billing Days (optional)</div>
                </div>
                <div className='base-setup-field-group'>
                  <div className='base-setup-field-small-value'>
                    <Control.select model='.billing_day' className='base-setup-field-select'>
                      {selectableBillingDays().map(i => <option key={i} value={i}>{ordinalizeBillingDay(i)}</option>)}
                      {railsEnv === 'test' && <option value='error'>Simulate Error</option>}
                    </Control.select>
                    <span className='base-setup-field-helper'>of the month</span>
                  </div>
                  {[0, 1, 2].map((index) => (
                    <div className='base-setup-field-small-value'>
                      <Control.select model={`.billing_days[${index}]`} className='base-setup-field-select'>
                        {<option key='' value='' />}
                        {selectableBillingDays().map(i => <option key={i} value={i}>{ordinalizeBillingDay(i)}</option>)}
                      </Control.select>
                    </div>
                  ))}
                </div>

                <div className='base-setup-field-group'>
                  <div className='base-setup-field-label'>
                    { 'Offer Additional Billing Day Options' }
                    { autopayDayChanges }
                  </div>
                </div>

                <div className='base-setup-field-group'>
                  <div className='base-setup-field-checkbox-value' />
                  <div className='base-setup-field-checkbox-value'>
                    <Control.checkbox
                      model='.offer_bi_monthly'
                      disabled={billingConfigForm.model.disable_autopay}
                    />
                  </div>
                  <div className='base-setup-field-inline-label'>
                    { '2 Monthly Withdrawals' }
                  </div>
                  {[0, 1].map((index) => (
                    <div className='base-setup-field-small-value'>
                      <Control.select model={`.bi_monthly_default[${index}]`} className='base-setup-field-select'>
                        {selectableBillingDays().map(i => <option key={i} value={i}>{ordinalizeBillingDay(i)}</option>)}
                      </Control.select>
                    </div>
                  ))}
                </div>
                <div className='base-setup-field-group'>
                  <div className='base-setup-field-checkbox-value' />
                  <div className='base-setup-field-checkbox-value'>
                    <Control.checkbox
                      model='.offer_four_monthly'
                      disabled={billingConfigForm.model.disable_autopay}
                    />
                  </div>
                  <div className='base-setup-field-inline-label'>
                    { '4 Monthly Withdrawals' }
                  </div>
                  {[0, 1, 2, 3].map((index) => (
                    <div className='base-setup-field-small-value'>
                      <Control.select model={`.four_monthly_default[${index}]`} className='base-setup-field-select'>
                        {selectableBillingDays().map(i => <option key={i} value={i}>{ordinalizeBillingDay(i)}</option>)}
                      </Control.select>
                    </div>
                  ))}
                </div>

                <div className='base-setup-field-group'>
                  <div className='base-setup-field-label'>Grace Period</div>
                  <div className='base-setup-field-value'>
                    <Control.select model='.grace_period' className='base-setup-field-select'>
                      {sequence(0, 15).map(i => <option key={i} value={i}>{i}</option>)}
                    </Control.select>
                    <span className='base-setup-field-helper'>days</span>
                  </div>
                </div>

                <div className='base-setup-field-group'>
                  <div className='base-setup-field-label'>
                    Overdue Threshold
                    <OverlayTrigger
                      placement='right'
                      overlay={(
                        <Tooltip id='tooltip-overdue-threshold'>
                          Minimum balance for which an account will be considered overdue. Balances less than or equal
                          to this amount will not be considered overdue when the grace period expires.
                        </Tooltip>
                      )}
                    >
                      <FontAwesome name='info-circle' preNbsp />
                    </OverlayTrigger>
                  </div>
                  <div className='base-setup-field-value'>
                    <Control.text
                      model='.overdue_threshold'
                      type='number'
                      controlProps={{ step: '0.01' }}
                      placeholder='0.00'
                      className='base-setup-field-input'
                    />
                  </div>
                </div>

                <div className='base-setup-field-group'>
                  <div className='base-setup-field-label'>Default Term for payer-level categories</div>
                  <div className='base-setup-field-value'>
                    <Control.select model='.current_school_year_id' className='base-setup-field-select'>
                      <option value="">Select term...</option>
                      {schoolYears.filter(schoolYear => schoolYear.billing_enabled)
                                  .map(schoolYear => <option value={schoolYear.id} key={schoolYear.id}>
                        {schoolYear.name}
                      </option>)}
                    </Control.select>
                  </div>
                </div>

                {
                  railsEnv !== 'production' && (
                    <div className='base-setup-field-group'>
                      <div className='base-setup-field-label'>Effective Date</div>
                      <div className='base-setup-field-value'>
                        <DateSelector
                          model='.test_effective_date'
                          modelValue={billingConfigForm.model.test_effective_date}
                          className='base-setup-field-input'
                        />
                        <div className='red'>For testing only, not available in production!</div>
                      </div>
                    </div>
                  )
                }
              </div>
            </div>

            {saveButton}

            <h2>Revenue Recognition</h2>

            <div className={'base-setup-fields'}>
              <div className={'base-setup-field-group'}>
                <div className='base-setup-field-value'>Fiscal Year Start Month</div>
                <div className='base-setup-field-inline-label'>Fiscal Year End Month</div>
              </div>
              <div className='base-setup-field-group'>
                <div className='base-setup-field-value'>
                  <Control.select
                    model='.fiscal_year_start_month'
                    className='base-setup-field-select'
                    onChange={updateFiscalYearEndMonth}
                  >
                    {MONTHS.map(([value, name]) =>
                      <option key={value} value={value}>{name}</option>
                    )}
                  </Control.select>
                </div>
                <div className='base-setup-field-value'>
                  <Control.select
                    model='.fiscal_year_end_month'
                    className='base-setup-field-select'
                    disabled={true}
                  >
                    {MONTHS.map(([value, name]) =>
                      <option key={value} value={value}>{name}</option>
                    )}
                  </Control.select>
                </div>

              </div>
              <div className={'base-setup-field-group'}>
                <div className='base-setup-field-value'>Revenue Recognition Start Month</div>
                <div className='base-setup-field-inline-label'>Revenue Recognition End Month</div>
              </div>
              <div className='base-setup-field-group'>
                <div className='base-setup-field-value'>
                  <Control.select
                    model='.revenue_recognition_start_month'
                    className='base-setup-field-select'
                  >
                    {MONTHS.map(([value, name]) =>
                      <option key={value} value={value}>{name}</option>
                    )}
                  </Control.select>
                </div>
                <div className='base-setup-field-value'>
                  <Control.select
                    model='.revenue_recognition_end_month'
                    className='base-setup-field-select'
                  >
                    {MONTHS.map(([value, name]) =>
                      <option key={value} value={value}>{name}</option>
                    )}
                  </Control.select>
                </div>
              </div>
              <div className={'base-setup-field-group'}>
                <div className='base-setup-field-label'>Default Revenue Recognition Allocation Rule</div>
                <div className='base-setup-field-value'>
                  <Control.select model='.revenue_recognition_strategy' className='base-setup-field-select'>
                    <option value={''}></option>
                    <option value={'at_once'}>At Once</option>
                    <option value={'equal_spread'}>Equal Spread</option>
                    <option value={'remaining_spread'}>Remaining Spread</option>
                    <option value={'spread_with_catchup'}>Spread With Catchup</option>
                  </Control.select>
                </div>
              </div>
            </div>

            {saveButton}

            <h2>Late Fee Setup</h2>

            <div className={'base-setup-fields'}>
              <div className={'base-setup-field-group'}>
                <div className={'base-setup-field-checkbox-value'}>
                  <Control.checkbox
                    model={'.late_fee_enabled'}
                  />
                </div>
                <div className={'base-setup-field-checkbox-label'}>
                  Automatic Late Fees
                  <InfoTooltip preNbsp>
                    Enabling this will automatically charge accounts that become overdue.  All accounts will have this setting enabled.
                    If you need to disable an account, please visit their overview page to disable it.
                  </InfoTooltip>
                  <span className="billing-config-autopay-changelog">
                    <LinkButton
                      content="View Changes"
                      onClick={() => showAudits(billingConfigForm.model.id, 'autopay_default')}
                    />
                  </span>
                </div>
              </div>

              <div className='base-setup-field-group'>
                <div className='base-setup-field-label'>
                  Late Fee Amount
                </div>
                <div className='base-setup-field-value'>
                  <Control.text
                    model='.late_fee_amount'
                    type='number'
                    controlProps={{ step: '0.01' }}
                    placeholder='0.00'
                    className='base-setup-field-input'
                  />
                </div>
              </div>

              <div className='base-setup-field-group'>
                <div className='base-setup-field-label'>
                  Late Fee by Percentage
                </div>
                <div className='base-setup-field-value'>
                  <Control.text
                    model='.late_fee_percentage'
                    type='number'
                    controlProps={{ step: '0.01' }}
                    placeholder='0.00'
                    className='base-setup-field-input'
                  />
                </div>
              </div>

              <div className='base-setup-field-group'>
                <div className='base-setup-field-label'>Late Fee Billing Category</div>
                <div className='base-setup-field-value'>
                  <Control.select model='.late_fee_billing_category_id' className='base-setup-field-select'>
                    <option value={''}></option>
                    {lateFeeBillingCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </Control.select>
                </div>
              </div>
            </div>

            {saveButton}

            <h2>Ledger Lock</h2>

            <div className={'base-setup-fields'}>
              <p>
                <em>
                  Entering a date will prevent any changes to the ledger prior to the set date.
                </em>
              </p>

              <div className='base-setup-field-group'>
                <div className='base-setup-field-label'>
                  Date
                  <span className='billing-config-autopay-changelog'>
                    <LinkButton
                      content='View Changes'
                      onClick={() => showAudits(billingConfigForm.model.id, 'ledger_lock')}
                    />
                  </span>
                </div>
                <div className='base-setup-field-value'>
                  <DateSelector
                    model='.ledger_lock'
                    modelValue={billingConfigForm.model.ledger_lock}
                    className='base-setup-field-input'
                  />
                </div>
              </div>
            </div>

            {saveButton}

            <h2>Autopay Setup</h2>

            <div className='base-setup-field-group'>
              <div className='base-setup-field-checkbox-value'>
                <Control.checkbox
                  model='.autopay_retry_enabled'
                  disabled={billingConfigForm.model.disable_autopay}
                />
              </div>
              <div className='base-setup-field-checkbox-label'>
                Retry Failed Autopay Payments
                <span className='billing-config-autopay-changelog'>
                    {
                      billingConfigForm.model.disable_autopay
                        ? <span className='billing-config-autopay-disabled'>View Changes</span>
                        : (
                          <LinkButton
                            content='View Changes'
                            onClick={() => showAudits(billingConfigForm.model.id, 'autopay_retry_enabled')}
                          />
                        )
                    }
                  </span>
              </div>
            </div>

            <div className='base-setup-fields'>
              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-value'>
                  <Control.checkbox
                    model='.autopay_default'
                    disabled={billingConfigForm.model.require_autopay}
                  />
                </div>
                <div className="base-setup-field-checkbox-label">
                  {defaultAutopayTooltip}
                  <span className="billing-config-autopay-changelog">
                    <LinkButton
                      content="View Changes"
                      onClick={() => showAudits(billingConfigForm.model.id, 'autopay_default')}
                    />
                  </span>
                </div>
              </div>

              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-value'>
                  <Control.checkbox
                    model='.require_autopay'
                    disabled={billingConfigForm.model.disable_autopay}
                  />
                </div>
                <div className='base-setup-field-checkbox-label'>
                  { requireAutopayTooltip }
                  <span className='billing-config-autopay-changelog'>
                    {
                      billingConfigForm.model.disable_autopay
                        ? <span className='billing-config-autopay-disabled'>View Changes</span>
                        : (
                          <LinkButton
                            content='View Changes'
                            onClick={() => showAudits(billingConfigForm.model.id, 'require_autopay')}
                          />
                        )
                    }
                  </span>
                </div>
              </div>

              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-value'>
                  <Control.checkbox
                    model='.require_autopay_zero_deposit'
                    disabled={!billingConfigForm.model.require_autopay}
                  />
                </div>
                <div className='base-setup-field-checkbox-label'>
                  { requireAutopayZeroDepositTooltip }
                  <span className='billing-config-autopay-changelog'>
                    {
                      billingConfigForm.model.disable_autopay
                        ? <span className='billing-config-autopay-disabled'>View Changes</span>
                        : (
                          <LinkButton
                            content='View Changes'
                            onClick={() => showAudits(billingConfigForm.model.id, 'require_autopay_zero_deposit')}
                          />
                        )
                    }
                  </span>
                </div>
              </div>

              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-value'>
                  <Control.checkbox
                    model='.disable_autopay'
                    disabled={billingConfigForm.model.require_autopay}
                  />
                </div>
                <div className='base-setup-field-checkbox-label'>
                  { disableAutopayTooltip }
                  <span className='billing-config-autopay-changelog'>
                    {
                      billingConfigForm.model.require_autopay
                        ? <span className='billing-config-autopay-disabled'>View Changes</span>
                        : (
                          <LinkButton
                            content='View Changes'
                            onClick={() => showAudits(billingConfigForm.model.id, 'disable_autopay')}
                          />
                        )
                    }
                  </span>
                </div>
              </div>
              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-label'>
                  { parentFacingPaymentOptionsTooltip }
                  { autopayDayChanges }
                </div>
              </div>
              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-value' />
                <div className='base-setup-field-checkbox-value'>
                  <Control.checkbox
                    model='.show_parents_additional_days'
                    disabled={billingConfigForm.model.disable_autopay}
                  />
                </div>
                <div className='base-setup-field-checkbox-label'>
                  { 'Additional Billing Days' }
                </div>
              </div>
              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-value' />
                <div className='base-setup-field-checkbox-value'>
                  <Control.checkbox
                    model='.show_parents_bi_monthly'
                    disabled={billingConfigForm.model.disable_autopay}
                  />
                </div>
                <div className='base-setup-field-checkbox-label'>
                  { '2 Monthly Withdrawals' }
                </div>
              </div>
              <div className='base-setup-field-group'>
                <div className='base-setup-field-checkbox-value' />
                <div className='base-setup-field-checkbox-value'>
                  <Control.checkbox
                    model='.show_parents_four_monthly'
                    disabled={billingConfigForm.model.disable_autopay}
                  />
                </div>
                <div className='base-setup-field-checkbox-label'>
                  { '4 Monthly Withdrawals' }
                </div>
              </div>
            </div>

            {saveButton}

            <h2>Email Notification Settings</h2>

            <div className='base-setup-fields'>
              <p>
                <em>
                  <FA icon={faInfoCircle} postNbsp />
                  Visit
                  {' '}
                  <a href={'/setup/billing_snippets'}>Communication &gt; Email Templates</a>
                  {' '}
                  to manage the content of these emails.
                </em>
              </p>

              <table className={'table'}>
                <thead>
                  <tr>
                    <th>Email Name</th>
                    <th style={{ width: '70%' }}>Frequency</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={hasError('statement_1_days') ? 'alert-danger' : null}>
                    <td>Upcoming Statement</td>
                    <td>
                      <NotificationDaysSelect model={'.statement_1_days'} />
                      {billingConfigForm.model.statement_1_days > 0 ? ' before billing date' : null}
                    </td>
                  </tr>
                  <tr className={hasError('statement_2_days') ? 'alert-danger' : null}>
                    <td>Upcoming Statement - Second Notice</td>
                    <td>
                      <NotificationDaysSelect model={'.statement_2_days'} />
                      {billingConfigForm.model.statement_2_days > 0 ? ' before billing date' : null}
                    </td>
                  </tr>
                  <tr className={hasError('statement_3_days') ? 'alert-danger' : null}>
                    <td>Upcoming Statement - Third Notice</td>
                    <td>
                      <NotificationDaysSelect model={'.statement_3_days'} />
                      {billingConfigForm.model.statement_3_days > 0 ? ' before billing date' : null}
                    </td>
                  </tr>
                  <tr>
                    <td>Account Overdue</td>
                    <td>
                      <span className={'base-setup-field-plaintext'} style={{ width: 'auto', display: 'inline-block' }}>
                        {billingConfigForm.model.grace_period}
                        {' '}
                        {pluralize(billingConfigForm.model.grace_period, 'day', 'days')}
                      </span>
                      {' '}
                      after billing date (grace period)
                    </td>
                  </tr>
                  <tr className={hasError('overdue_2_days') ? 'alert-danger' : null}>
                    <td>Account Overdue - Second Notice</td>
                    <td>
                      <NotificationDaysSelect model={'.overdue_2_days'} />
                      {billingConfigForm.model.overdue_2_days > 0 ? ' after billing date' : null}
                    </td>
                  </tr>
                  <tr className={hasError('overdue_3_days') ? 'alert-danger' : null}>
                    <td>Account Overdue - Third Notice</td>
                    <td>
                      <NotificationDaysSelect model={'.overdue_3_days'} />
                      {billingConfigForm.model.overdue_3_days > 0 ? ' after billing date' : null}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {saveButton}
          </Form>
        )}
      />


      {railsEnv !== 'production' && <div>
        <hr />

        <h2>Billing Accounts</h2>

        <p><em className="red">NOTE: this account summary is for testing only and is not available in production.</em></p>

        <table className="table billing-account-list">
          <thead>
          <tr>
            <th>Contact</th>
            <th>Billing Date</th>
            <th>Status</th>
            <th>Message</th>
            <th>Overdue At</th>
            <th/>
          </tr>
          </thead>
          <tbody>
          {billingAccounts.map((account, i) => <tr key={i}>
            <td><a href={`/contacts/${account.contact.id}/billing`}>{account.contact.name}</a></td>
            <td>{account.billing_date || 'n/a'}</td>
            <td>{account.billing_date ? account.status : '-'}</td>
            <td>{account.message}</td>
            <td>{account.overdue_at}</td>
            <td>
              {account.billing_date !== null && <button className="btn btn-sm btn-danger"
                      title="Reset Account"
                      onClick={() => reset(account)}>
                <FontAwesome name="undo"/>
              </button>}
            </td>
          </tr>)}
          </tbody>
        </table>
      </div>}

      <ModalRoot
        modals={{
          BillingDayConfirmModal,
          ConfirmDisable,
          ConfirmEnrollment,
          ResetAccountModal,
          RevenueRecognitionConfirmModal
        }}
      />
    </React.Fragment>
  );
};

BillingConfig.propTypes = {
  appConfig: PropTypes.shape({
    railsEnv: PropTypes.string
  }),
  billingAccounts: PropTypes.arrayOf(PropTypes.shape({
    billing_date: PropTypes.string,
    contact: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    }),
    message: PropTypes.string,
    overdue_at: PropTypes.string,
    status: PropTypes.string
  })),
  billingConfig: PropTypes.shape({
    start_year: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  billingConfigForm: PropTypes.shape({
    form: PropTypes.shape({
      billing_day: PropTypes.shape({
        pristine: PropTypes.bool
      }),
      disable_autopay: PropTypes.shape({
        pristine: PropTypes.bool
      }),
      require_autopay: PropTypes.shape({
        pristine: PropTypes.bool
      }),
      saving: PropTypes.bool
    }),
    model: PropTypes.shape({
      billing_day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      disable_autopay: PropTypes.bool,
      errors: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
      id: PropTypes.string,
      require_autopay: PropTypes.bool,
      test_effective_date: PropTypes.string,
      ledger_lock: PropTypes.string,
      grace_period: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      overdue_2_days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      overdue_3_days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      statement_1_days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      statement_2_days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      statement_3_days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      current_school_year_id: PropTypes.string
    })
  }),
  duckMeta: PropTypes.shape({
    billingConfigForm: PropTypes.shape({
      ready: PropTypes.bool
    })
  }),
  reset: PropTypes.func,
  save: PropTypes.func,
  showAudits: PropTypes.func
};

BillingConfig.defaultProps = {
  appConfig: {
    railsEnv: ''
  },
  billingAccounts: [],
  billingConfig: {
    start_year: ''
  },
  billingConfigForm: {
    form: {
      billing_day: {
        pristine: true
      },
      disable_autopay: {
        pristine: true
      },
      require_autopay: {
        pristine: true
      }
    },
    model: {
      billing_day: '1',
      disable_autopay: false,
      errors: {},
      id: '',
      require_autopay: false,
      test_effective_date: '',
      ledger_lock: ''
    },
    saving: false
  },
  duckMeta: {
    billingConfigForm: {
      ready: false
    }
  },
  reset: () => false,
  save: () => false,
  showAudits: () => false
};

const mapDuckToProps = () => {
  const billingConfig = singleDuck(billingConfigDuck, 'current');

  return {
    customer: singleDuck(customersDuck, 'current'),
    billingAccounts: billingAccountsDuck,
    billingConfig,
    billingConfigForm: withForm(billingConfig, billingConfigFormDuck),
    schoolYears: filterDuck(schoolYearsDuck, { excluding_archived: true }),
    lateFeeBillingCategories: filterDuck(billingCategories, {non_contact_only: true, active_only:true})
  };
};

const mapStateToProps = (state) => ({
  appConfig: state.appConfig
});

// noinspection JSUnusedGlobalSymbols
const mapDispatchToProps = (dispatch) => ({
  save: ({ model, form }) => {
    const handleSave = (m) => dispatch(billingConfigFormDuck.actions.save(m))
      .then(() => Utility.displayFlashMessages({ success: 'Billing settings saved.' }))
      .then(() => dispatch(billingAccountsDuck.actions.load()))
      .catch(response => {
        if (!response.billing_config) {
          Utility.displayFlashMessages({ error: 'Sorry, something went wrong.' });
        }
      });

    const all = [];

    if (!form.billing_day.pristine && model.billing_day !== 'error') {
      const billingDayPromise = new Promise((resolve, reject) => {
        dispatch(modals.actions.show('BillingDayConfirmModal', {
          onSave: (notify) => {
            dispatch(modals.actions.hide());
            resolve({ notify_billing_day_changed: notify });
          },
          onClose: () => {
            dispatch(modals.actions.hide());
            reject();
          }
        }));
      });

      all.push(billingDayPromise);
    }

    if (!form.require_autopay.pristine && model.require_autopay === true) {
      const requireAutopayPromise = new Promise((resolve, reject) => {
        dispatch(modals.actions.show('ConfirmEnrollment', {
          onConfirm: () => {
            dispatch(modals.actions.hide());
            resolve();
          },
          onClose: () => {
            dispatch(modals.actions.hide());
            reject();
          }
        }));
      });

      all.push(requireAutopayPromise);
    }

    if (!form.disable_autopay.pristine && model.disable_autopay === true) {
      const disableAutopayPromise = new Promise((resolve, reject) => {
        dispatch(modals.actions.show('ConfirmDisable', {
          onConfirm: () => {
            dispatch(modals.actions.hide());
            resolve();
          },
          onClose: () => {
            dispatch(modals.actions.hide());
            reject();
          }
        }));
      });

      all.push(disableAutopayPromise);
    }

    let result = { ...model };

    Utility.hideNotifications();

    return Promise.all(all).then((extraParams) => {
      extraParams.forEach((params) => {
        if (params) {
          result = { ...result, ...params };
        }
      });

      dispatch(actions.setPristine('billingConfigForm.model'));

      handleSave(result);
    }, () => {
      Utility.displayFlashMessages({ error: 'Billing config not saved. You must confirm all changes.' });
    });
  },
  reset: (account) => dispatch(modals.actions.show('ResetAccountModal', {account})),
  showAudits: (id, field, resource = 'billing_configs') => dispatch(modals.actions.show('AuditViewer', {
    id,
    field,
    resource
  }))
});

export default duckify(mapDuckToProps, mapStateToProps, mapDispatchToProps)(BillingConfig);
